'use client'
import type {
  LocaleString,
  Video as SanityVideo,
} from '@/.generated/sanityTypes/graphql'
import './contentBlock.css'
import type { Locale } from '@/i18n'
import { useLocale } from 'next-intl'

import React, { useRef } from 'react'

import {
  type ColorList,
  type StoryLayoutKind,
  mapColorlistToColor,
  mapStoryLayoutToBlockLayout,
} from './layoutStyles'
import Title from '@/components/Title'
import BlockLayout from './BlockLayout'
import { withNewLines } from '@/lib/newline'
import ReactMarkdown from 'react-markdown'
import { Vimeo } from '@monorepo/helper/Vimeo'

type ContentBlockProps = {
  title: LocaleString
  content: LocaleString
  videos?: Array<SanityVideo>
  colorlist?: ColorList
  layout?: StoryLayoutKind
  url: string
}

const ContentBlock = ({
  title,
  content,
  colorlist,
  layout,
  videos,
}: ContentBlockProps) => {
  const locale = useLocale() as Locale
  const videoRef = useRef<HTMLDivElement>(null)
  return (
    <BlockLayout
      color={mapColorlistToColor(colorlist)}
      layout={mapStoryLayoutToBlockLayout(layout)}
    >
      <div className="max-w-1400 color-title px-4 py-8 sm:px-20 md:px-0">
        <Title>{withNewLines(title?.[locale])}</Title>
        {content?.[locale] && (
          <ReactMarkdown className="color-subtitle mx-auto max-w-xl text-xl md:text-2xl lg:text-3xl">
            {content[locale] || ''}
          </ReactMarkdown>
        )}
        {videos?.length && (
          <div className="mt-8 flex flex-col space-y-14 md:mt-16 md:flex-row md:space-x-12 md:space-y-0 lg:justify-between">
            {videos.map((video: any, videoIndex: number) => {
              return (
                <div
                  key={videoIndex}
                  className="min-w-2/5 w-full flex-1 text-center"
                >
                  <div className="mx-auto max-w-2xl" ref={videoRef}>
                    <Vimeo
                      className="absolute left-0 top-0 my-0 mb-2 mt-0 block h-full w-full"
                      urlOrId={`https://player.vimeo.com/video/${video.videoId?.[locale]}?portrait=0&color=DA291C&byline=0&title=0&speed=0&fun=0`}
                      title={title?.[locale] || undefined}
                    />
                  </div>
                  {video.title?.[locale] && (
                    <div className="color-secondary pt-4 text-xl">
                      <p>{video.title?.[locale]}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </BlockLayout>
  )
}

export default ContentBlock
